import { GET_FLOW_QUERY_KEY, useGetFlowQuery } from '@/state/queries/flows/use-get-flow';
import { Box, Container, CircularProgress, Alert, Snackbar } from '@mui/material';
import { useParams, useSearchParams, useNavigate } from 'react-router';
import FixedNavigationBar from '@/components/common/fixed-top-bar';
import PaymentProcessingModal from '@/components/common/payment-processing.modal';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useEffect, useMemo, useState, useRef, useLayoutEffect } from 'react';
import { normalizeEventFlowsResponse, useGetEventFlowsQuery } from '@/state/queries/flows/use-get-event-flows.ts';
import { useGetEventContributorsQuery } from '@/state/queries/contributors/use-get-event-contributors';
import { useEventSessionsQuery } from '@/state/queries/sessions/use-event-sessions.ts';
import SlotList from './slot-list';
import { useEventStore } from '@/state/stores/use-event-store';
import { useTranslation } from 'react-i18next';
import EditDetailsDialog from './edit-details-dialog';
import FlowBar from './flow-bar';
import { FlowControlSectionHeader } from './flow-control-section-header';
import { FlowControlTabErrorBoundary } from './flow-detail-tabs/flow-control-tab-error-boundary';
import { useFlowDetailLiveContextSync } from './use-flow-detail-live-context-sync';
import { useFlowDetailMediaSettingsSync } from './use-flow-detail-media-settings-sync';
import { FlowDetailSectionHeader, type FlowDetailSectionHeaderAccent } from './flow-detail-section-header';
import {
  FlowScheduleTab,
  FlowSettingsTab,
  FlowSlotAssetsTab,
  FlowContributorsTab,
  FlowConfigurationTab,
  FlowControlTab,
  SCHEDULE_SLOT_LIST_HIDE_FLOW_START_ON_SMALL_SCREEN,
  SCHEDULE_SLOT_LIST_HIDE_END_ITEM_MEDIA_INDICATOR,
  SCHEDULE_SLOT_LIST_SHOW_DRAG_HANDLE,
  SCHEDULE_SLOT_LIST_COMPACT_INFO_ALERT,
} from './flow-detail-tabs';
import { FlowSlotAssetsSlotListColumn } from './flow-detail-tabs/flow-slot-assets-slot-list-column';
import {
  slotListColumnHeaderInsetSx,
  slotListColumnOuterSx,
  slotListScrollAreaSx,
} from './flow-detail-tabs/flow-slot-list-column-sx';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import type { FlowDto, FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import { EVENT_SLOT_TYPE } from '@/types/dtos/events/event-slot-content.enum';
import { useGetProductsQuery } from '@/state/queries/orders/use-get-products.ts';
import { useProductsStore } from '@/state/stores/use-products-store.ts';
import type { EventDto, EventSlotDto } from '@/types/dtos/events/response/event.dto';
import { useGetEventQuery } from '@/state/queries/events/use-get-event';
import { ensureFlowGlobalHandlers } from '@/hooks/use-flow-socket';
import { useContributorSocketSync } from '@/hooks/use-contributor-socket-sync';
import { useEventPublicationSocketSync } from '@/hooks/use-event-publication-socket-sync';
import { useAuth } from '@clerk/clerk-react';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import { useModeStore } from '@/state/stores/use-mode-store';
import { useFlowControlNarrowViewStore } from '@/state/stores/use-flow-control-narrow-view-store';
import { useGetSessionQuery } from '@/state/queries/sessions/use-get-session';
import { ServerError } from '@/types/errors/server.error';
import { getObjectId, resolveFlowEntityIdForPlayers, resolveLiveBindingFlowId } from '@/helpers/object.id.helper';
import { getEntityRefFromFlow } from '@/helpers/entity-ref.helper';
import { useGetLiveContextStateQuery } from '@/state/queries/player/use-get-live-context-state';
import { FlowControlMediaAgentRecoveryAlert } from './flow-detail-tabs/flow-control-media-agent-recovery-alert';
import { useFlowMode } from './use-flow-mode';
import {
  debugFlow,
  pickNewerSession,
  resolveEventIdForContributors,
  resolveEventIdForLists,
  resolveEventSlotForFlow,
  resolveSessionOriginIdFromFlowSlot,
  selectFlowByMode,
} from './flow-detail.utils';
import { findUpdatedSlotForSelection } from './flow-slot-selection-match';
import { type FlowDetailMode, FLOW_DETAIL_MODE_TO_VIEW, VIEW_TO_FLOW_DETAIL_MODE } from './flow-detail.types';
import { useScreenSize } from '@/hooks/use-screen-size';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { FlowBasicsEventSlotTiming } from '@/components/events/detail/tabs/flows/grid/flow-basics-event-slot-timing';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import { liveContextVmFingerprint } from '@/helpers/live-context-vm.helper';
import { getOriginEventId } from '@/helpers/event.helper';
import { useFlowPlanModeHeaderState } from './use-flow-plan-mode-header-state';

export default function FlowDetail() {
  const { t } = useTranslation();
  const { flowId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isWideScreen, isSmallScreen } = useScreenSize({ breakpoint: 860 });
  const flowBarHeight = 52;
  const activeOrganization = useActiveOrganizationStore((s) => s.activeOrganization);

  // URL parameter handling for maintaining state on reload
  const viewParam = searchParams.get('view'); // 'slot', 'settings', 'contributors'
  const slotIdParam = searchParams.get('slotId');
  const eventSlotIdParam = searchParams.get('eventSlotId'); // Specific eventSlot ID from event
  const contextTypeParam = searchParams.get('contextType'); // 'event', 'organization', etc.
  const contextIdParam = searchParams.get('contextId'); // ID of the context entity

  useEventPublicationSocketSync(contextTypeParam === 'event' && contextIdParam ? contextIdParam : undefined);
  useContributorSocketSync();

  const {
    data: flow,
    flows: flowsFromOriginQuery,
    isLoading: isFlowLoading,
    isError: isFlowError,
    error: flowError,
    dataUpdatedAt: flowDataUpdatedAt,
    refetch: refetchFlow,
  } = useGetFlowQuery(flowId, eventSlotIdParam || undefined, { refetchOnMount: true, staleTime: 0 });

  useFlowDetailLiveContextSync({ flow, eventSlotId: eventSlotIdParam });
  useFlowDetailMediaSettingsSync({ flowId: flowId ?? undefined, eventSlotId: eventSlotIdParam });

  const bindingFlowId = flow ? resolveLiveBindingFlowId(flow, eventSlotIdParam ?? undefined) : undefined;
  const recoveryEntityRef = useMemo(() => {
    if (!bindingFlowId || !flow?.liveContext?.isRunning) {
      return undefined;
    }
    return getEntityRefFromFlow(bindingFlowId, flow.liveContext);
  }, [bindingFlowId, flow]);
  const { data: liveContextStateForRecovery } = useGetLiveContextStateQuery(recoveryEntityRef, {
    enabled: Boolean(recoveryEntityRef),
  });

  useEffect(() => {
    debugFlow('[FlowDetail] flow data', { isArray: Array.isArray(flow), flow, eventSlotIdParam });
  }, [flow, eventSlotIdParam]);
  const flowPlan = useFlowStore((s) => s.flowPlan);
  const flowOrganize = useFlowStore((s) => s.flowOrganize);
  const flowMode = useModeStore((s) => s.flowMode);
  const setMode = useModeStore((s) => s.setMode);
  const flowModeValue = useFlowMode();

  // Try to get event ID from flow parent or URL context - independent from event page store
  const eventIdForQuery = (() => {
    // First priority: contextIdParam from URL (most reliable, available immediately)
    if (contextTypeParam === 'event' && contextIdParam) return contextIdParam;
    // Second priority: flow.parent (from fetched flow data)
    if (flow?.parent) {
      if (typeof flow.parent === 'string') return flow.parent;
      if (typeof flow.parent === 'object' && 'id' in flow.parent) return flow.parent.id;
    }
    return undefined;
  })();
  const {
    data: fetchedEvent,
    isError: isEventError,
    error: eventError,
    isLoading: isEventLoading,
    normalized: normalizedEvent,
  } = useGetEventQuery(eventIdForQuery);
  useEffect(() => {
    if (fetchedEvent) {
      if (normalizedEvent?.eventPlan) {
        setEventPlan(normalizedEvent.eventPlan);
      }
      if (normalizedEvent?.eventOrganize) {
        setEventOrganize(normalizedEvent.eventOrganize);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedEvent, normalizedEvent]);

  // Prefer HTTP-fetched event (full document); otherwise use embedded flow.parent.
  const event = useMemo(() => {
    const flowParentEvent =
      flow?.parent && typeof flow.parent === 'object' && 'title' in flow.parent ? flow.parent : undefined;
    const fetchedEventObj =
      fetchedEvent && typeof fetchedEvent === 'object' && 'title' in fetchedEvent ? fetchedEvent : undefined;

    return fetchedEventObj ?? flowParentEvent;
  }, [flow?.parent, fetchedEvent]);

  // For single-flow events (multiFlow=false), automatically use the eventSlot if not already in URL
  useEffect(() => {
    // Only proceed if we don't already have an eventSlotIdParam
    if (eventSlotIdParam) return;

    // Use fetchedEvent as primary source (most reliable), fallback to event from flow.parent
    const eventToCheck: EventDto | undefined =
      fetchedEvent ||
      (event && typeof event === 'object' && 'multiFlow' in event && 'slots' in event
        ? (event as EventDto)
        : undefined);

    // Only proceed if we have event data with multiFlow=false and flow data
    if (!eventToCheck || !flow || eventToCheck.multiFlow !== false) return;

    // Find the eventSlot for this flow
    const flowOriginId = flow.origin ? getObjectId(flow.origin) : undefined;
    const flowEventSlot = eventToCheck.slots?.find(
      (slot) =>
        slot.contentType === EVENT_SLOT_TYPE.FLOW &&
        (slot.content === flow.id || (flowOriginId && slot.content === flowOriginId)),
    );

    // If we found an eventSlot, update the URL to include it
    if (flowEventSlot) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('eventSlotId', flowEventSlot.id);
      // Preserve other params
      if (contextTypeParam) newParams.set('contextType', contextTypeParam);
      if (contextIdParam) newParams.set('contextId', contextIdParam);
      setSearchParams(newParams, { replace: true });
    }
  }, [fetchedEvent, event, flow, eventSlotIdParam, searchParams, setSearchParams, contextTypeParam, contextIdParam]);

  // Determine eventId for queries: prioritize contextIdParam (available immediately from URL),
  // then event.id, fetchedEvent.id, or eventIdForQuery
  // This ensures queries can run immediately when navigating from flow list/dashboard
  const eventIdForLists = resolveEventIdForLists({
    contextTypeParam,
    contextIdParam,
    event: event as EventDto | undefined,
    fetchedEvent: fetchedEvent as EventDto | undefined,
    eventIdForQuery,
  });

  const eventIdForContributors = resolveEventIdForContributors({
    event: event as EventDto | undefined,
    fetchedEvent: fetchedEvent as EventDto | undefined,
    eventIdForLists,
  });

  useEffect(() => {
    if (!eventIdForLists) {
      debugFlow('[FlowDetail] eventIdForLists is undefined', {
        contextIdParam,
        contextTypeParam,
        'event?.id': event?.id,
        'fetchedEvent?.id': fetchedEvent?.id,
        eventIdForQuery,
        'flow?.parent': flow?.parent,
      });
    }
  }, [eventIdForLists, contextIdParam, contextTypeParam, event, fetchedEvent, eventIdForQuery, flow]);

  const eventMultiFlow = useMemo(() => {
    const eventSource =
      fetchedEvent ?? (event && typeof event === 'object' && 'multiFlow' in event ? (event as EventDto) : undefined);
    return eventSource?.multiFlow;
  }, [fetchedEvent, event]);

  // Multi-flow events need all flows; single-flow events get PLAN+ORGANIZE from useGetFlowQuery.
  const shouldFetchAllEventFlows = !!eventIdForLists && eventMultiFlow === true;

  const { data: eventFlowsList } = useGetEventFlowsQuery(eventIdForLists, {
    enabled: shouldFetchAllEventFlows,
  });
  const flows = eventMultiFlow === true ? eventFlowsList : flowsFromOriginQuery;
  const { data: contributors } = useGetEventContributorsQuery(eventIdForContributors);
  const {
    data: sessions,
    refetch: refetchSessions,
    normalized: normalizedSessions,
  } = useEventSessionsQuery({ eventId: eventIdForLists, loadAllModes: true });

  const { data: catalogProducts } = useGetProductsQuery();
  const setProducts = useProductsStore((s) => s.setProducts);

  const hasNavigatedOnErrorRef = useRef(false);
  const isUpdatingFlowRef = useRef(false);
  const lastFlowUpdateRef = useRef<string | null>(null);

  const routeKey = `${flowId ?? ''}|${eventSlotIdParam ?? ''}`;
  const [routeKeyChangeTs, setRouteKeyChangeTs] = useState(0);
  useEffect(() => {
    setRouteKeyChangeTs(Date.now());
  }, [routeKey]);

  // Robust readiness check - ensure flow is not an array
  const isReady = !isFlowLoading && !!flow && !Array.isArray(flow);
  const flowOriginId =
    flow?.origin && typeof flow.origin === 'object'
      ? flow.origin.id
      : typeof flow?.origin === 'string'
        ? flow.origin
        : undefined;
  const flowMatchesRoute = !!flowId && !!flow && (flow.id === flowId || flowOriginId === flowId);
  const hasFreshFlowData = flowDataUpdatedAt >= routeKeyChangeTs;
  // If flow failed, we're waiting for event query to complete before navigation (only if we have an event ID to query)
  const isWaitingForErrorNavigation =
    isFlowError && !isFlowLoading && eventIdForQuery && isEventLoading && !hasNavigatedOnErrorRef.current;

  const setFlow = useFlowStore((s) => s.setFlow);
  const storeFlow = useFlowStore((s) => s.flow);
  const setFlowPlan = useFlowStore((s) => s.setFlowPlan);
  const setFlowOrganize = useFlowStore((s) => s.setFlowOrganize);
  const setEvent = useEventStore((s) => s.setEvent);
  const eventPlan = useEventStore((s) => s.eventPlan);
  const eventOrganize = useEventStore((s) => s.eventOrganize);
  const setEventPlan = useEventStore((s) => s.setEventPlan);
  const setEventOrganize = useEventStore((s) => s.setEventOrganize);
  const setEventFlows = useEventStore((s) => s.setEventFlows);
  const setEventFlowsPlan = useEventStore((s) => s.setEventFlowsPlan);
  const setEventFlowsOrganize = useEventStore((s) => s.setEventFlowsOrganize);
  const setEventContributors = useEventStore((s) => s.setEventContributors);
  const setEventSessions = useEventStore((s) => s.setEventSessions);
  const setEventSessionsPlan = useEventStore((s) => s.setEventSessionsPlan);
  const setEventSessionsOrganize = useEventStore((s) => s.setEventSessionsOrganize);
  const selectedSlot = useFlowStore((s) => s.selectedSlot);
  const setSelectedSlot = useFlowStore((s) => s.setSelectedSlot);
  const { getToken } = useAuth();

  useLayoutEffect(() => {
    if (!flowId) return;
    setFlow(undefined);
    setSelectedSlot(undefined);
    useFlowStore.getState().setFlowPlan(undefined);
    useFlowStore.getState().setFlowOrganize(undefined);
  }, [flowId, setFlow, setSelectedSlot]);

  const hasSlotContentChanges = (currentSlots?: FlowSlotDto[], nextSlots?: FlowSlotDto[]) => {
    if (!currentSlots || !nextSlots) return false;
    if (currentSlots.length !== nextSlots.length) return true;
    if (currentSlots.some((slot, index) => slot.id !== nextSlots[index]?.id)) return true;
    const currentById = new Map(currentSlots.map((slot) => [slot.id, slot]));
    return nextSlots.some((nextSlot) => {
      const currentSlot = currentById.get(nextSlot.id);
      if (!currentSlot) return true;
      if (typeof currentSlot.content !== typeof nextSlot.content) return true;
      if (typeof nextSlot.content === 'string' || typeof currentSlot.content === 'string') {
        return currentSlot.content !== nextSlot.content;
      }
      if (!nextSlot.content || !currentSlot.content) return nextSlot.content !== currentSlot.content;
      if ('id' in nextSlot.content && 'id' in currentSlot.content && nextSlot.content.id !== currentSlot.content.id) {
        return true;
      }
      if (
        'version' in nextSlot.content &&
        'version' in currentSlot.content &&
        nextSlot.content.version !== currentSlot.content.version
      ) {
        return true;
      }
      if (
        'title' in nextSlot.content &&
        'title' in currentSlot.content &&
        nextSlot.content.title !== currentSlot.content.title
      ) {
        return true;
      }
      return false;
    });
  };

  const hasLiveContextChanges = (
    currentLiveContext?: FlowDto['liveContext'],
    nextLiveContext?: FlowDto['liveContext'],
  ) => {
    if (!currentLiveContext && !nextLiveContext) return false;
    if (!currentLiveContext || !nextLiveContext) return true;
    if (currentLiveContext.isRunning !== nextLiveContext.isRunning) return true;
    if (currentLiveContext.showMode !== nextLiveContext.showMode) return true;
    const currentVmKey = currentLiveContext.vms?.map(liveContextVmFingerprint).join('|') ?? '';
    const nextVmKey = nextLiveContext.vms?.map(liveContextVmFingerprint).join('|') ?? '';
    return currentVmKey !== nextVmKey;
  };

  const [showFlowDetails, setShowFlowDetails] = useState<FlowDetailMode | undefined>(undefined);
  const [flowDetailsDirection, setFlowDetailsDirection] = useState<'up' | 'down' | undefined>(undefined);
  const [isNavigating, setIsNavigating] = useState(false);
  const [showTimingAlert, setShowTimingAlert] = useState(false);
  const [showPublishAlert, setShowPublishAlert] = useState(false);
  const [overlayExplicitlyOpened, setOverlayExplicitlyOpened] = useState(false);
  const narrowViewMode = useFlowControlNarrowViewStore((s) => s.mode);
  const setNarrowViewMode = useFlowControlNarrowViewStore((s) => s.setMode);
  const prevIsSmallScreenRef = useRef(isSmallScreen);
  const prevViewParamRef = useRef<string | null>(null);
  const prevSlotIdParamRef = useRef<string | null>(null);
  const prevPerformAccessSnapshotRef = useRef<string>('');
  const userInitiatedSlotIdRef = useRef<string | null>(null);

  const { hasAccess: hasPerformFeatureAccess, isLoading: isPerformFeatureAccessLoading } = useFeatureAccess('perform');
  const performTabsAllowed = !isPerformFeatureAccessLoading && hasPerformFeatureAccess;

  // Helper functions to update URL parameters
  // Preserves important params like eventSlotId, contextType, contextId
  const updateUrlParams = (view?: string, slotId?: string) => {
    const newParams = new URLSearchParams(searchParams);

    if (view) {
      newParams.set('view', view);
    } else {
      newParams.delete('view');
    }

    if (slotId) {
      newParams.set('slotId', slotId);
    } else {
      newParams.delete('slotId');
    }

    // Preserve eventSlotId, contextType, and contextId if they exist
    // These are important for maintaining timing data and context on reload
    if (eventSlotIdParam) {
      newParams.set('eventSlotId', eventSlotIdParam);
    }
    if (contextTypeParam) {
      newParams.set('contextType', contextTypeParam);
    }
    if (contextIdParam) {
      newParams.set('contextId', contextIdParam);
    }

    setSearchParams(newParams, { replace: true });
  };

  const handleSlotSelection = (slot: FlowSlotDto, isUserInitiated = true) => {
    setIsNavigating(true);
    setSelectedSlot(slot);
    // When on flowControl or flowSlotAssets, stay on that tab and show slot details there
    if (showFlowDetails !== 'flowControl' && showFlowDetails !== 'flowSlotAssets') {
      setShowFlowDetails(undefined);
    }
    // Mark overlay as explicitly opened if user-initiated (regardless of screen size)
    // This allows overlay to open on small screens when user clicks, but prevents auto-opening
    // flowControl uses overlay only on small screens (wide: inline FlowControlTab; !isWideScreen gates dialog)
    const usesOverlay =
      showFlowDetails !== 'flowSettings' &&
      showFlowDetails !== 'flowContributors' &&
      showFlowDetails !== 'flowConfiguration';
    if (isUserInitiated && usesOverlay) {
      if (!(showFlowDetails === 'flowControl' && !isWideScreen) && showFlowDetails !== 'flowSlotAssets') {
        setOverlayExplicitlyOpened(true);
      }
      // Track that this slot was selected by user action, so URL handler doesn't reset overlay state
      userInitiatedSlotIdRef.current = slot.id;
    } else {
      // Programmatic selection: keep narrow control overlay open when auto-following playback.
      const preserveControlOverlay = showFlowDetails === 'flowControl' && !isWideScreen;
      if (!preserveControlOverlay) {
        setOverlayExplicitlyOpened(false);
      }
      userInitiatedSlotIdRef.current = null;
    }
    // Update URL parameters immediately and synchronously
    // Preserve existing params like eventSlotId, contextType, contextId
    const newParams = new URLSearchParams(searchParams);
    const viewParam =
      showFlowDetails === 'flowControl' ? 'control' : showFlowDetails === 'flowSlotAssets' ? 'assets' : 'slot';
    newParams.set('view', viewParam);
    newParams.set('slotId', slot.id);
    // Immediate URL update — preserve RR history state (idx/key); nulling it
    // breaks subsequent rematches after react-router 7.15+ history metadata.
    const newUrl = `${window.location.pathname}?${newParams.toString()}`;
    window.history.replaceState(window.history.state, '', newUrl);
    // Force a re-render by updating searchParams state
    setSearchParams(newParams, { replace: true });
    // Reset navigation flag after a short delay to allow URL param handler to skip
    setTimeout(() => {
      setIsNavigating(false);
    }, 100);
  };

  const handleFlowDetailsSelection = (mode: React.SetStateAction<FlowDetailMode | undefined>) => {
    const resolvedMode = typeof mode === 'function' ? mode(showFlowDetails) : mode;
    if (resolvedMode) {
      if ((resolvedMode === 'flowConfiguration' || resolvedMode === 'flowControl') && !performTabsAllowed) {
        if (isPerformFeatureAccessLoading) return;
        setShowFlowDetails('flowSettings');
        setSelectedSlot(undefined);
        setOverlayExplicitlyOpened(false);
        updateUrlParams('settings');
        return;
      }
      setShowFlowDetails(resolvedMode);
      // Always clear selectedSlot when opening flow details
      setSelectedSlot(undefined);
      // Mark overlay as explicitly opened for modes that use it. flowControl is inline on wide screens
      // (FlowControlTab) but on narrow screens perform lives in EditDetailsDialog — opening Control from
      // the slot-list header / flow bar must set this or the perform panel never appears.
      const usesOverlay =
        resolvedMode !== 'flowSettings' &&
        resolvedMode !== 'flowContributors' &&
        resolvedMode !== 'flowConfiguration' &&
        resolvedMode !== 'flowControl' &&
        resolvedMode !== 'flowSlotAssets' &&
        resolvedMode !== 'flowSchedule';
      setOverlayExplicitlyOpened(usesOverlay);
      updateUrlParams(FLOW_DETAIL_MODE_TO_VIEW[resolvedMode]);
    } else {
      setShowFlowDetails(undefined);
      setSelectedSlot(undefined);
      setOverlayExplicitlyOpened(false);
      updateUrlParams();
    }
  };

  const handleCloseDetails = () => {
    // Control, Assets, Schedule (narrow): back closes overlay but stays on tab (SlotList)
    if (
      showFlowDetails === 'flowControl' ||
      showFlowDetails === 'flowSlotAssets' ||
      showFlowDetails === 'flowSchedule'
    ) {
      if (showFlowDetails === 'flowControl') {
        setNarrowViewMode('list');
      }
      setOverlayExplicitlyOpened(false);
      // Keep showFlowDetails so main area shows SlotList
      // Keep selectedSlot for highlighting when returning to list
      updateUrlParams(
        showFlowDetails === 'flowControl' ? 'control' : showFlowDetails === 'flowSlotAssets' ? 'assets' : 'schedule',
      );
      return;
    }

    // On small screens, when closing slot details overlay, keep slot selected and scroll to it
    if (isSmallScreen && selectedSlot && !showFlowDetails) {
      // Scroll to slot after overlay closes
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-slot-id="${selectedSlot.id}"]`);
        if (!el) {
          // Try alternative selectors
          const selectors = [
            `[data-flow-slot-id="${selectedSlot.contentType === 'Session' ? selectedSlot.content?.id : selectedSlot.id}"]`,
            `#flow-slot-${selectedSlot.contentType === 'Session' ? selectedSlot.content?.id : selectedSlot.id}`,
            `#slot-${selectedSlot.id}`,
          ];
          const foundEl = selectors.map((sel) => document.querySelector(sel)).find((node): node is Element => !!node);
          if (foundEl && 'scrollIntoView' in foundEl) {
            foundEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } else if ('scrollIntoView' in el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
      // Keep slot selected for highlighting, but close overlay
      setOverlayExplicitlyOpened(false);
      setShowFlowDetails(undefined);
      // Preserve slot ID in URL for state consistency (overlay won't reopen because overlayExplicitlyOpened is false)
      updateUrlParams('slot', selectedSlot.id);
    } else {
      // On larger screens or when closing flow details (settings/contributors), clear selection
      setSelectedSlot(undefined);
      setShowFlowDetails(undefined);
      setOverlayExplicitlyOpened(false);
      updateUrlParams();
    }
  };

  const handleSlotDeletedOnSmallScreen = () => {
    // On small screens, when a slot is deleted, close the dialog and show slot list with no selection
    setSelectedSlot(undefined);
    setShowFlowDetails(undefined);
    setOverlayExplicitlyOpened(false);
    updateUrlParams();
  };

  /** After deleting a slot from the flow schedule tab, stay on schedule (do not switch to settings). */
  const handleSlotDeletedFromSchedule = () => {
    setSelectedSlot(undefined);
    setShowFlowDetails('flowSchedule');
    setOverlayExplicitlyOpened(false);
    updateUrlParams('schedule');
  };

  /** After publishing a session from the flow schedule tab, keep schedule context (tab + URL). */
  const handleSessionPublishSuccessOnSchedule = () => {
    const slot = useFlowStore.getState().selectedSlot;
    void refetchFlow();
    void refetchSessions();
    setOverlayExplicitlyOpened(false);
    setShowFlowDetails(undefined);
    if (slot) {
      setSelectedSlot(slot);
      updateUrlParams('slot', slot.id);
    } else {
      setShowFlowDetails('flowSchedule');
      updateUrlParams('schedule');
    }
  };

  const handleShowTimingAlert = () => {
    setShowTimingAlert(true);
  };

  const handleShowPublishAlert = () => {
    setShowPublishAlert(true);
  };

  const eventSessionsPlan = useEventStore((s) => s.eventSessionsPlan);
  const eventSessionsOrganize = useEventStore((s) => s.eventSessionsOrganize);
  const selectedSessionOriginId = useMemo(
    () =>
      resolveSessionOriginIdFromFlowSlot(selectedSlot, {
        eventSessionsPlan,
        eventSessionsOrganize,
        flowPlan,
      }),
    [selectedSlot, eventSessionsPlan, eventSessionsOrganize, flowPlan],
  );

  const selectedPlanSessionFromStore = useMemo(() => {
    if (!selectedSessionOriginId) return undefined;
    return eventSessionsPlan?.find((s) => {
      const originId = s.origin ? getObjectId(s.origin) : s.id;
      return originId === selectedSessionOriginId || s.id === selectedSessionOriginId;
    });
  }, [eventSessionsPlan, selectedSessionOriginId]);

  const selectedOrganizeSessionFromStore = useMemo(() => {
    if (!selectedSessionOriginId) return undefined;
    return eventSessionsOrganize?.find((s) => {
      const originId = s.origin ? getObjectId(s.origin) : s.id;
      return originId === selectedSessionOriginId || s.id === selectedSessionOriginId;
    });
  }, [eventSessionsOrganize, selectedSessionOriginId]);

  const shouldFetchSelectedSession = !!selectedSessionOriginId;
  const { data: selectedSessionFromBackendPlan, error: selectedSessionPlanError } = useGetSessionQuery(
    shouldFetchSelectedSession ? selectedSessionOriginId : undefined,
    'PLAN',
  );
  const selectedPlanNotFound =
    selectedSessionPlanError instanceof ServerError && selectedSessionPlanError.statusCode === 404;
  const shouldFetchSelectedOrganize =
    shouldFetchSelectedSession &&
    (flowModeValue === 'ORGANIZE' ||
      flowModeValue === 'PERFORM' ||
      (selectedPlanNotFound && !!selectedOrganizeSessionFromStore));
  const { data: selectedSessionFromBackendOrganize, error: selectedSessionOrganizeError } = useGetSessionQuery(
    shouldFetchSelectedOrganize ? selectedSessionOriginId : undefined,
    'ORGANIZE',
  );

  const slotSession = useMemo(() => {
    if (selectedSlot?.contentType !== 'Session' || !selectedSlot.content || typeof selectedSlot.content !== 'object') {
      return undefined;
    }
    const embedded = selectedSlot.content;
    const backendMode =
      flowModeValue === 'PLAN'
        ? 'PLAN'
        : flowModeValue === 'ORGANIZE' || flowModeValue === 'PERFORM'
          ? 'ORGANIZE'
          : undefined;
    const allowOrganizeFallback = flowModeValue === 'PLAN' && selectedPlanNotFound;
    if (
      embedded.mode === backendMode ||
      (allowOrganizeFallback && embedded.mode === 'ORGANIZE') ||
      (flowModeValue === 'PLAN' && !embedded.mode)
    ) {
      return embedded;
    }
    return undefined;
  }, [selectedSlot, flowModeValue, selectedPlanNotFound]);

  const isSelectedSessionDeleted =
    selectedPlanNotFound &&
    !selectedPlanSessionFromStore &&
    !slotSession &&
    (!shouldFetchSelectedOrganize ||
      (selectedSessionOrganizeError instanceof ServerError && selectedSessionOrganizeError.statusCode === 404)) &&
    !selectedOrganizeSessionFromStore;

  const selectedSessionPlan = useMemo(
    () => pickNewerSession(selectedPlanSessionFromStore, selectedSessionFromBackendPlan),
    [selectedPlanSessionFromStore, selectedSessionFromBackendPlan],
  );
  const selectedSessionOrganize = useMemo(
    () => pickNewerSession(selectedOrganizeSessionFromStore, selectedSessionFromBackendOrganize),
    [selectedOrganizeSessionFromStore, selectedSessionFromBackendOrganize],
  );

  const selectedSessionContent = useMemo(() => {
    if (selectedSlot?.contentType !== 'Session') {
      return selectedSlot?.content;
    }

    if (isSelectedSessionDeleted) {
      return undefined;
    }

    if (flowModeValue === 'PLAN') {
      return selectedSessionPlan ?? slotSession ?? selectedSessionOrganize;
    }
    if (flowModeValue === 'ORGANIZE' || flowModeValue === 'PERFORM') {
      return selectedSessionOrganize ?? selectedSessionPlan ?? slotSession;
    }
    return selectedSessionPlan ?? selectedSessionOrganize ?? slotSession;
  }, [
    selectedSlot,
    isSelectedSessionDeleted,
    flowModeValue,
    selectedSessionPlan,
    selectedSessionOrganize,
    slotSession,
  ]);

  const slotTitle = useMemo(() => {
    const defaultVal = t('flows.detail.editDetails.title');
    if (!selectedSlot) return defaultVal;

    // Use storeFlow.slot (updated by useEffect with correct eventSlot) instead of flow.slot from React Query
    const flowSlot = storeFlow?.slot || flow?.slot;
    const time =
      selectedSlot.computedOffsetMinutes !== undefined && flowSlot?.startOffsetMinutes !== undefined
        ? minuteOffsetToTime(flowSlot.startOffsetMinutes + selectedSlot.computedOffsetMinutes)
        : t('common.N/A');
    switch (selectedSlot.contentType) {
      case 'Session': {
        // Use resolved sessionContent instead of selectedSlot.content directly
        const sessionTitle =
          typeof selectedSessionContent === 'object' && selectedSessionContent?.title
            ? selectedSessionContent.title
            : undefined;
        if (isSelectedSessionDeleted) {
          return `${time} | ${t('common.sessionDeleted')}`;
        }
        return sessionTitle ? `${time} | ${sessionTitle}` : `Slot content ${t('common.notPublishedYet')}.`;
      }
      case 'Break':
        return selectedSlot?.title ? `${time} | ${selectedSlot?.title}` : t('common.N/A');
      default:
        return defaultVal;
    }
  }, [selectedSlot, selectedSessionContent, storeFlow, flow, t]);

  // For mobile header: title = content title only; subtitle = "start - end (duration min)"
  const slotMobileTitle = useMemo(() => {
    if (!selectedSlot) return undefined;
    switch (selectedSlot.contentType) {
      case 'Session': {
        // Use resolved sessionContent instead of selectedSlot.content directly
        const mobileSessionTitle =
          typeof selectedSessionContent === 'object' && selectedSessionContent?.title
            ? selectedSessionContent.title
            : undefined;
        return mobileSessionTitle || `Slot content ${t('common.notPublishedYet')}.`;
      }
      case 'Break':
        return selectedSlot?.title || t('common.N/A');
      default:
        return `Slot content ${t('common.notPublishedYet')}.`;
    }
  }, [selectedSlot, selectedSessionContent, t]);

  const slotMobileSubtitle = useMemo(() => {
    // Use storeFlow.slot (updated by useEffect with correct eventSlot) instead of flow.slot from React Query
    const flowSlot = storeFlow?.slot || flow?.slot;
    if (!selectedSlot || selectedSlot.computedOffsetMinutes === undefined || flowSlot?.startOffsetMinutes === undefined)
      return undefined;
    const startOffset = flowSlot.startOffsetMinutes + selectedSlot.computedOffsetMinutes;
    const duration = selectedSlot.computedDurationMinutes ?? selectedSlot.plannedDurationMinutes ?? 0;
    const endOffset = startOffset + duration;
    const startTime = minuteOffsetToTime(startOffset);
    const endTime = minuteOffsetToTime(endOffset);
    return `${startTime} - ${endTime} (${duration} minutes)`;
  }, [selectedSlot, storeFlow, flow]);

  // Wide layout only: auto-select first slot when opening schedule (narrow: no selection).
  const firstSlot = useMemo(() => {
    const slots = (storeFlow?.slots ?? flow?.slots)?.filter((s) => s.contentType !== 'Gap') ?? [];
    const timed = slots.filter(
      (s) => !(s.isUntimed ?? (s.plannedOffsetMinutes === undefined && s.computedOffsetMinutes === undefined)),
    );
    const untimed = slots.filter(
      (s) => s.isUntimed ?? (s.plannedOffsetMinutes === undefined && s.computedOffsetMinutes === undefined),
    );
    const orderedTimed = timed.sort(
      (a, b) =>
        (a.computedOffsetMinutes ?? a.plannedOffsetMinutes ?? 0) -
        (b.computedOffsetMinutes ?? b.plannedOffsetMinutes ?? 0),
    );
    const ordered = [...untimed, ...orderedTimed];
    return ordered[0];
  }, [storeFlow?.slots, flow?.slots]);

  /** Keeps wide FlowScheduleTab mounted when selectedSlot is briefly cleared during refetch/sync (publish/delete). */
  const showScheduleMainLayout = useMemo(
    () =>
      showFlowDetails === 'flowSchedule' ||
      (showFlowDetails === undefined && !!selectedSlot) ||
      (showFlowDetails === undefined && (viewParam === 'schedule' || viewParam === 'slot')),
    [showFlowDetails, selectedSlot, viewParam],
  );

  const {
    label: schedulePlanHeaderLabel,
    color: schedulePlanHeaderColor,
    isLiveContextRunning,
  } = useFlowPlanModeHeaderState(flowId, 'schedule');

  const resolveFlowDetailHeaderAccent = (accent: FlowDetailSectionHeaderAccent): FlowDetailSectionHeaderAccent =>
    isLiveContextRunning ? 'warning' : accent;

  useEffect(() => {
    if (showFlowDetails === 'flowSchedule' && !selectedSlot && firstSlot && !isSmallScreen) {
      handleSlotSelection(firstSlot, false);
    }
  }, [showFlowDetails, firstSlot, isSmallScreen]); // Intentionally exclude selectedSlot and handleSlotSelection to run only when tab opens

  useEffect(() => {
    if (catalogProducts) {
      setProducts(catalogProducts);
    }
  }, [catalogProducts, setProducts]);

  // Handle URL parameters on flow load - this should run first
  // Also handles socket updates to ensure the correct eventSlot is preserved
  // IMPORTANT: When eventSlotIdParam is provided, ONLY use fetchedEvent (most reliable source)
  // This prevents flickering from multiple sources updating flow.slot at different times
  useEffect(() => {
    if (!flowId) return;
    const currentMode = flowMode[flowId];
    const hasPlanMode = !!flowPlan;
    const hasOrganizeMode = !!flowOrganize;
    const isCurrentModeValid =
      (currentMode === 'PLAN' && hasPlanMode) ||
      (currentMode === 'ORGANIZE' && hasOrganizeMode) ||
      (currentMode === 'PERFORM' && hasOrganizeMode);
    const defaultMode = hasPlanMode ? 'PLAN' : hasOrganizeMode ? 'ORGANIZE' : undefined;
    const nextMode = isCurrentModeValid ? currentMode : defaultMode;

    if (nextMode && currentMode !== nextMode) {
      setMode({
        flowMode: {
          ...flowMode,
          [flowId]: nextMode,
        },
      });
    }
  }, [flowId, flowMode, flowPlan, flowOrganize, setMode]);

  useEffect(() => {
    if (isUpdatingFlowRef.current) return;
    if (!flowMatchesRoute || !hasFreshFlowData) return;
    const baseFlow = selectFlowByMode({
      flowMode,
      flowId,
      flowPlan,
      flowOrganize,
      fallbackFlow: flow,
    });

    debugFlow('[FlowDetail] resolve baseFlow', {
      flowId,
      flowMode: flowMode?.[flowId ?? ''],
      hasFlow: !!flow,
      hasFlowPlan: !!flowPlan,
      hasFlowOrganize: !!flowOrganize,
      baseFlowId: baseFlow?.id,
      baseFlowVersion: baseFlow?.version,
      baseFlowSlots: baseFlow?.slots?.length,
    });

    if (!baseFlow) return;

    const eventFromParent =
      baseFlow.parent && typeof baseFlow.parent === 'object' && 'slots' in baseFlow.parent
        ? (baseFlow.parent as EventDto)
        : undefined;
    const eventByMode = baseFlow.mode === 'PLAN' ? eventPlan : eventOrganize;
    const eventForSlot =
      eventByMode && eventByMode.mode === baseFlow.mode
        ? eventByMode
        : eventFromParent?.mode === baseFlow.mode
          ? eventFromParent
          : undefined;

    let resolvedSlot = resolveEventSlotForFlow({
      event: eventForSlot,
      flow: baseFlow,
      eventSlotIdParam,
    });
    if (!resolvedSlot && eventSlotIdParam && baseFlow.slot?.id === eventSlotIdParam) {
      // Fallback to backend-provided slot when event data isn't available yet
      resolvedSlot = baseFlow.slot;
    }

    const currentStoreFlow = useFlowStore.getState().flow;

    // Check for media changes early to avoid early return when only media changed
    const mediaChangedEarly = (() => {
      const currentMedia = currentStoreFlow?.media;
      const nextMedia = baseFlow.media;
      if (!currentMedia && !nextMedia) return false;
      if (!currentMedia || !nextMedia) return true;
      if (currentMedia.length !== nextMedia.length) return true;
      const currentById = new Map(currentMedia.map((media) => [media.id, media]));
      return nextMedia.some((nextMediaItem) => {
        const currentMediaItem = currentById.get(nextMediaItem.id);
        if (!currentMediaItem) return true;
        return (
          currentMediaItem.id !== nextMediaItem.id ||
          currentMediaItem.title !== nextMediaItem.title ||
          currentMediaItem.isRequested !== nextMediaItem.isRequested ||
          JSON.stringify(currentMediaItem.assets) !== JSON.stringify(nextMediaItem.assets)
        );
      });
    })();

    const liveContextChangedEarly = hasLiveContextChanges(currentStoreFlow?.liveContext, baseFlow.liveContext);
    const mediaPlaylistsChangedEarly =
      JSON.stringify(currentStoreFlow?.mediaPlaylists ?? null) !== JSON.stringify(baseFlow.mediaPlaylists ?? null);

    if (
      currentStoreFlow &&
      currentStoreFlow.id === baseFlow.id &&
      currentStoreFlow.version === baseFlow.version &&
      currentStoreFlow.slot?.id === resolvedSlot?.id &&
      currentStoreFlow.slot?.version === resolvedSlot?.version &&
      !mediaChangedEarly &&
      !liveContextChangedEarly &&
      !mediaPlaylistsChangedEarly
    ) {
      return;
    }

    let flowToSet = baseFlow;

    if (eventSlotIdParam) {
      // When eventSlotIdParam is set, only keep the matching slot (or undefined if not available yet)
      flowToSet = {
        ...baseFlow,
        slot: resolvedSlot,
      };
    } else {
      // Master flow context: do not attach any eventSlot to avoid fallback selection
      flowToSet = {
        ...baseFlow,
        slot: undefined,
      };
    }

    // Preserve slot content from current store flow when updating
    // This ensures that if the new flow has slots with incomplete content (e.g., just IDs),
    // we preserve the full content objects from the current store flow
    if (currentStoreFlow && flowToSet.slots && currentStoreFlow.slots && currentStoreFlow.mode === flowToSet.mode) {
      flowToSet = {
        ...flowToSet,
        slots: flowToSet.slots.map((newSlot) => {
          const existingSlot = currentStoreFlow.slots?.find((s) => s.id === newSlot.id);
          // If existing slot has content (object) and new slot only has ID (string), preserve existing content
          if (
            existingSlot &&
            existingSlot.content &&
            typeof existingSlot.content === 'object' &&
            (typeof newSlot.content === 'string' || !newSlot.content)
          ) {
            return { ...newSlot, content: existingSlot.content };
          }
          return newSlot;
        }),
      };
    }

    if (
      currentStoreFlow?.version !== undefined &&
      flowToSet.version !== undefined &&
      flowToSet.version < currentStoreFlow.version
    ) {
      return;
    }

    // Check if flow needs to be updated (by ID, version, or if slot has changed)
    // Also check if eventSlot version changed (when event updates with fresh eventSlot data)
    const slotsChanged = hasSlotContentChanges(currentStoreFlow?.slots, flowToSet.slots);
    const mediaChanged = (() => {
      const currentMedia = currentStoreFlow?.media;
      const nextMedia = flowToSet.media;
      if (!currentMedia && !nextMedia) return false;
      if (!currentMedia || !nextMedia) return true;
      if (currentMedia.length !== nextMedia.length) return true;
      const currentById = new Map(currentMedia.map((media) => [media.id, media]));
      return nextMedia.some((nextMediaItem) => {
        const currentMediaItem = currentById.get(nextMediaItem.id);
        if (!currentMediaItem) return true;
        return (
          currentMediaItem.id !== nextMediaItem.id ||
          currentMediaItem.title !== nextMediaItem.title ||
          JSON.stringify(currentMediaItem.assets) !== JSON.stringify(nextMediaItem.assets)
        );
      });
    })();
    const liveContextChanged = hasLiveContextChanges(currentStoreFlow?.liveContext, flowToSet.liveContext);
    const mediaPlaylistsChanged =
      JSON.stringify(currentStoreFlow?.mediaPlaylists ?? null) !== JSON.stringify(flowToSet.mediaPlaylists ?? null);
    const needsUpdate =
      !currentStoreFlow ||
      currentStoreFlow.id !== flowToSet.id ||
      currentStoreFlow.version !== flowToSet.version ||
      currentStoreFlow.slot?.id !== flowToSet.slot?.id ||
      currentStoreFlow.slot?.version !== flowToSet.slot?.version ||
      JSON.stringify(currentStoreFlow.slot?.publishedModes) !== JSON.stringify(flowToSet.slot?.publishedModes) ||
      slotsChanged ||
      mediaChanged ||
      liveContextChanged ||
      mediaPlaylistsChanged;

    if (needsUpdate) {
      // Include media info in updateKey to ensure updates when media changes
      const mediaKey = flowToSet.media?.map((m) => `${m.id}:${m.title}`).join('|') || '';
      const mediaPlaylistsKey = JSON.stringify(flowToSet.mediaPlaylists ?? null);
      const liveContextVmKey = flowToSet.liveContext?.vms?.map(liveContextVmFingerprint).join('|') ?? '';
      const liveContextKey = flowToSet.liveContext
        ? `${flowToSet.liveContext.isRunning}-${flowToSet.liveContext.showMode}-${liveContextVmKey}`
        : 'no-live-context';
      const updateKey = `${flowToSet.id}-${flowToSet.version}-${flowToSet.slot?.id}-${flowToSet.slot?.version}-${mediaKey}-${mediaPlaylistsKey}-${liveContextKey}`;
      if (lastFlowUpdateRef.current !== updateKey) {
        lastFlowUpdateRef.current = updateKey;
        isUpdatingFlowRef.current = true;
        setFlow(flowToSet);
        debugFlow('[FlowDetail] setFlow', {
          flowId: flowToSet.id,
          flowVersion: flowToSet.version,
          slotId: flowToSet.slot?.id,
          slotVersion: flowToSet.slot?.version,
          slotsLength: flowToSet.slots?.length,
          mediaLength: flowToSet.media?.length,
        });
        setTimeout(() => {
          isUpdatingFlowRef.current = false;
        }, 0);
      }
    }

    // Update selectedSlot when flow data changes (e.g., moderators, session data updates)
    // Preserve overlay state - don't close overlay when data updates
    if (selectedSlot && flowToSet.slots) {
      const updatedSelectedSlot = findUpdatedSlotForSelection(flowToSet.slots, selectedSlot);
      if (updatedSelectedSlot) {
        // Only update the slot data, don't change overlay state
        setSelectedSlot(updatedSelectedSlot);
      }
    }

    // Check if URL parameters have actually changed (navigation) vs just flow data update
    // On first render, prevViewParamRef.current will be null, so we need to handle initial load
    const isInitialLoad = prevViewParamRef.current === null;
    const performAccessSnapshot = `${isPerformFeatureAccessLoading}:${hasPerformFeatureAccess}`;
    const urlParamsChanged =
      isInitialLoad ||
      prevViewParamRef.current !== viewParam ||
      prevSlotIdParamRef.current !== slotIdParam ||
      prevPerformAccessSnapshotRef.current !== performAccessSnapshot;

    // Handle URL parameters on flow load - only when URL params actually change (navigation), not on data updates
    // Skip when isNavigating is true (user-initiated navigation in progress) to avoid overriding user actions
    if (viewParam && flowToSet.slots && !isNavigating && urlParamsChanged) {
      if (viewParam === 'slot' && slotIdParam) {
        const slot = flowToSet.slots.find((s) => s.id === slotIdParam);
        if (slot) {
          setSelectedSlot(slot);
          setShowFlowDetails(undefined);
          // Check if this URL param was set by a user click (user-initiated action)
          // If so, preserve the overlay state that was set by handleSlotSelection
          // Otherwise, this is direct URL navigation, so set overlay state accordingly
          const isUserInitiatedNavigation = userInitiatedSlotIdRef.current === slotIdParam;
          if (!isUserInitiatedNavigation) {
            // Direct URL navigation - on small screens, don't open overlay - only focus the slot
            // On wide screens, overlay is always visible so this doesn't matter
            setOverlayExplicitlyOpened(isSmallScreen ? false : true);
          }
          // Clear the user-initiated ref after processing, so subsequent URL changes are treated as direct navigation
          if (isUserInitiatedNavigation) {
            userInitiatedSlotIdRef.current = null;
          }
        }
      } else if (
        (viewParam === 'control' || viewParam === 'assets') &&
        slotIdParam &&
        VIEW_TO_FLOW_DETAIL_MODE[viewParam]
      ) {
        const slot = flowToSet.slots.find((s) => s.id === slotIdParam);
        if (slot) {
          if (viewParam === 'control' && !performTabsAllowed) {
            if (!isPerformFeatureAccessLoading) {
              setSelectedSlot(slot);
              setShowFlowDetails(undefined);
              updateUrlParams('slot', slot.id);
              const isUserInitiatedNavigation = userInitiatedSlotIdRef.current === slotIdParam;
              if (!isUserInitiatedNavigation) {
                setOverlayExplicitlyOpened(isSmallScreen ? false : true);
              }
              if (isUserInitiatedNavigation) {
                userInitiatedSlotIdRef.current = null;
              }
            }
          } else {
            setSelectedSlot(slot);
            setShowFlowDetails(VIEW_TO_FLOW_DETAIL_MODE[viewParam]);
            const isUserInitiatedNavigation = userInitiatedSlotIdRef.current === slotIdParam;
            if (!isUserInitiatedNavigation) {
              setOverlayExplicitlyOpened(isSmallScreen ? false : true);
            }
            if (isUserInitiatedNavigation) {
              userInitiatedSlotIdRef.current = null;
            }
          }
        }
      } else if (viewParam && VIEW_TO_FLOW_DETAIL_MODE[viewParam] && !slotIdParam) {
        const m = VIEW_TO_FLOW_DETAIL_MODE[viewParam];
        const needsPerformFeature = m === 'flowConfiguration' || m === 'flowControl';
        if (needsPerformFeature && !performTabsAllowed) {
          if (!isPerformFeatureAccessLoading) {
            setShowFlowDetails('flowSettings');
            updateUrlParams('settings');
            setOverlayExplicitlyOpened((prev) => prev || !isSmallScreen);
            userInitiatedSlotIdRef.current = null;
          }
        } else {
          setShowFlowDetails(m);
          setOverlayExplicitlyOpened((prev) => prev || !isSmallScreen);
          userInitiatedSlotIdRef.current = null;
        }
      }

      // Only update refs when we actually processed URL params (not when skipped due to isNavigating)
      prevViewParamRef.current = viewParam;
      prevSlotIdParamRef.current = slotIdParam;
    } else if (!isNavigating) {
      // When not navigating and URL params haven't changed, still update refs to track current state
      // This handles the case where flow data updates but URL stays the same
      prevViewParamRef.current = viewParam;
      prevSlotIdParamRef.current = slotIdParam;
    }
    prevPerformAccessSnapshotRef.current = performAccessSnapshot;
  }, [
    flow?.id,
    flow?.version,
    flow?.slot?.id,
    flow?.slot?.version,
    flow?.slot?.startOffsetMinutes,
    flow?.media,
    flow?.slots,
    flow?.mediaPlaylists,
    flow?.liveContext?.isRunning,
    flow?.liveContext?.showMode,
    flowPlan?.id,
    flowPlan?.version,
    flowPlan?.media,
    flowPlan?.slots,
    flowPlan?.mediaPlaylists,
    flowPlan?.liveContext?.isRunning,
    flowPlan?.liveContext?.showMode,
    flowOrganize?.id,
    flowOrganize?.version,
    flowOrganize?.media,
    flowOrganize?.slots,
    flowOrganize?.mediaPlaylists,
    flowOrganize?.liveContext?.isRunning,
    flowMode,
    flowId,
    setFlow,
    viewParam,
    slotIdParam,
    isSmallScreen,
    isNavigating,
    eventSlotIdParam,
    eventPlan?.id,
    eventOrganize?.id,
    flowMatchesRoute,
    hasFreshFlowData,
    hasPerformFeatureAccess,
    isPerformFeatureAccessLoading,
  ]);

  useEffect(() => {
    if (isPerformFeatureAccessLoading) return;
    if (!hasPerformFeatureAccess && (showFlowDetails === 'flowConfiguration' || showFlowDetails === 'flowControl')) {
      setShowFlowDetails('flowSettings');
      setOverlayExplicitlyOpened(false);
      updateUrlParams('settings');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateUrlParams/setOverlay: same pattern as sibling effects in this file
  }, [isPerformFeatureAccessLoading, hasPerformFeatureAccess, showFlowDetails]);

  // Narrow Control: restore the last view mode (list / program+list / program+preview) from the
  // persisted store when opening Perform, and keep the overlay in sync with that mode.
  useEffect(() => {
    if (showFlowDetails !== 'flowControl' || isWideScreen) {
      return;
    }

    setOverlayExplicitlyOpened(narrowViewMode !== 'list');
  }, [showFlowDetails, isWideScreen, narrowViewMode]);

  // Remove debug log of selectedSlot changes

  // External entry (e.g. from event) often omits `view`; default to Settings on all breakpoints so the correct tab is selected.
  // If `view` is missing but a slot is selected (flow schedule + slot), restore slot URL instead of jumping to settings
  // (can happen after publish/refetch or transient URL loss).
  useEffect(() => {
    if (!viewParam) {
      if (selectedSlot) {
        updateUrlParams('slot', selectedSlot.id);
        setShowFlowDetails(undefined);
        setOverlayExplicitlyOpened(false);
        return;
      }
      setShowFlowDetails('flowSettings');
      updateUrlParams('settings');
    }
  }, [viewParam, selectedSlot]);

  // Handle viewport changes - clear explicit open flag only when transitioning from large to small
  useEffect(() => {
    // Only clear if viewport actually changed from large to small (not if already small)
    if (isSmallScreen && !prevIsSmallScreenRef.current) {
      // When viewport changes to small screen, don't auto-open overlay
      setOverlayExplicitlyOpened(false);
    }
    // Update ref for next comparison
    prevIsSmallScreenRef.current = isSmallScreen;
  }, [isSmallScreen]);

  useEffect(() => {
    if (event && typeof event === 'object' && 'title' in event) {
      setEvent(event as EventDto);
      // Ensure global flow socket handlers are initialized early for real-time updates
      (async () => {
        const token = await getToken();
        await ensureFlowGlobalHandlers(token ?? undefined);
      })();
    }
  }, [event, setEvent, getToken]);

  // Store sessions in event store when loaded
  useEffect(() => {
    if (sessions) {
      const { planSessions, organizeSessions, preferredSessions } = normalizedSessions;
      if (planSessions.length > 0) {
        setEventSessionsPlan(planSessions);
      }
      // Always set (including []) so invite checks use organize list, not publishedModes fallback.
      setEventSessionsOrganize(organizeSessions);
      if (preferredSessions.length > 0) {
        setEventSessions(preferredSessions);
      }
    }
  }, [sessions, normalizedSessions, setEventSessions, setEventSessionsPlan, setEventSessionsOrganize]);

  // Store contributors in event store when loaded
  useEffect(() => {
    if (contributors) {
      setEventContributors(contributors);
    }
  }, [contributors, setEventContributors]);

  // Auto-add eventSlotId has been disabled to avoid re-adding when users remove it.

  // Ensure eventFlows in the store are populated when landing directly on a flow
  // Merges intelligently with existing eventFlows to avoid overwriting fresher data
  // IMPORTANT: When eventSlotIdParam is provided, ONLY use fetchedEvent to prevent wrong eventSlot assignment
  useEffect(() => {
    if (flows && event) {
      // When eventSlotIdParam is provided, wait for fetchedEvent before setting eventFlows
      // This prevents assigning wrong eventSlot which could cause flickering
      if (eventSlotIdParam && !fetchedEvent) {
        // Wait for fetchedEvent to load before setting eventFlows
        return;
      }

      const currentEventFlows = useEventStore.getState().eventFlows;

      // When eventSlotIdParam is provided, ONLY use fetchedEvent (same source as main flow.slot logic)
      // This prevents assigning wrong eventSlot to eventFlows which could cause flickering
      const eventToUseForSlots = eventSlotIdParam ? fetchedEvent : (event as EventDto);

      const flowsWithSlot = flows.map((f) => {
        // If eventSlotId is provided in URL, use that specific eventSlot from fetchedEvent
        // Otherwise, find the first matching eventSlot (backward compatibility)
        const flowSlot =
          eventSlotIdParam && eventToUseForSlots
            ? eventToUseForSlots.slots?.find(
                (slot: EventSlotDto) => slot.id === eventSlotIdParam && slot.contentType === EVENT_SLOT_TYPE.FLOW,
              )
            : (event as EventDto).slots?.find(
                (slot: EventSlotDto) =>
                  (slot.content === f.id || slot.content === f.origin) && slot.contentType === EVENT_SLOT_TYPE.FLOW,
              );

        // If we have existing eventFlows, check if this flow already exists with fresher data
        if (currentEventFlows) {
          const existingFlow = currentEventFlows.find(
            (ef) => ef.id === f.id || ef.origin === f.id || (typeof ef.origin === 'object' && ef.origin?.id === f.id),
          );

          if (existingFlow) {
            // Only use query data if it's fresher (higher version) or if versions are undefined
            const shouldUseQueryData =
              (f.version !== undefined && existingFlow.version !== undefined && f.version > existingFlow.version) ||
              f.version === undefined ||
              existingFlow.version === undefined;

            if (!shouldUseQueryData) {
              // Keep existing flow if it's fresher, but preserve correct eventSlot if eventSlotIdParam is provided
              if (eventSlotIdParam && flowSlot) {
                return {
                  ...existingFlow,
                  slot: flowSlot, // Use correct eventSlot from fetchedEvent
                };
              }
              return existingFlow;
            }
          }
        }

        return {
          ...f,
          slot: flowSlot ?? f.slot,
        };
      });

      const { planFlows, organizeFlows } = normalizeEventFlowsResponse(flowsWithSlot);
      if (planFlows.length > 0) {
        setEventFlowsPlan(planFlows);
      }
      if (organizeFlows.length > 0) {
        setEventFlowsOrganize(organizeFlows);
      }
      setEventFlows(planFlows.length > 0 ? planFlows : organizeFlows.length > 0 ? organizeFlows : flowsWithSlot);
      // Ensure global flow socket handlers are initialized for real-time updates
      (async () => {
        const token = await getToken();
        await ensureFlowGlobalHandlers(token ?? undefined);
      })();
    }
  }, [flows, event, setEventFlows, setEventFlowsPlan, setEventFlowsOrganize, getToken, eventSlotIdParam, fetchedEvent]);

  // Update eventFlows with fresh current flow data when flow loads/changes
  // This ensures that when navigating between flows, the current flow's fresh data is in eventFlows
  // Uses version comparison to avoid race conditions - only updates if new data is fresher
  // IMPORTANT: When eventSlotIdParam is provided, ONLY use fetchedEvent (same source as main flow.slot logic)
  useEffect(() => {
    if (flow && event) {
      // When eventSlotIdParam is provided, ONLY use fetchedEvent (single source of truth)
      // This prevents race conditions and flickering from multiple sources
      if (eventSlotIdParam && !fetchedEvent) {
        // Wait for fetchedEvent to load before updating eventFlows
        return;
      }

      // When eventSlotIdParam is provided, ONLY use fetchedEvent
      // Otherwise, use fetchedEvent if available, or fall back to event from flow.parent
      const eventToUse = eventSlotIdParam
        ? fetchedEvent
        : fetchedEvent || (event && typeof event === 'object' && 'slots' in event ? (event as EventDto) : undefined);
      const eventFlows = useEventStore.getState().eventFlows;
      if (eventFlows) {
        // Check if this flow is already in eventFlows
        const flowIndex = eventFlows.findIndex(
          (f) => f.id === flow.id || f.origin === flow.id || (typeof f.origin === 'object' && f.origin?.id === flow.id),
        );

        if (flowIndex !== -1) {
          const existingFlow = eventFlows[flowIndex];
          // Only update if the new flow has a higher version (fresher data)
          // This prevents race conditions where stale data overwrites fresh data
          const shouldUpdate =
            flow.version !== undefined && existingFlow.version !== undefined && flow.version > existingFlow.version;

          // Also update if versions are equal but slots differ (data might have changed)
          const slotsChanged = JSON.stringify(flow.slots) !== JSON.stringify(existingFlow.slots);

          if (shouldUpdate || slotsChanged || flow.version === undefined || existingFlow.version === undefined) {
            const updatedFlows = [...eventFlows];
            // If eventSlotId is provided, use that specific eventSlot by ID only
            // Otherwise find the first matching one by content
            const flowSlot =
              eventSlotIdParam && eventToUse
                ? eventToUse.slots?.find(
                    (slot: EventSlotDto) => slot.id === eventSlotIdParam && slot.contentType === EVENT_SLOT_TYPE.FLOW,
                  )
                : eventToUse?.slots?.find(
                    (slot: EventSlotDto) =>
                      (slot.content === flow.id ||
                        slot.content === flow.origin ||
                        (typeof flow.origin === 'object' && flow.origin?.id && slot.content === flow.origin.id)) &&
                      slot.contentType === EVENT_SLOT_TYPE.FLOW,
                  );
            // Preserve slot content from existing flow when updating
            // Backend may send slots with incomplete content (just IDs), so preserve full content objects
            // IMPORTANT: When eventSlotIdParam is provided, use the correct eventSlot from fetchedEvent
            // Don't fall back to existing flow's slot which might be wrong
            let flowWithPreservedContent = {
              ...flow,
              slot: eventSlotIdParam && flowSlot ? flowSlot : (flowSlot ?? updatedFlows[flowIndex].slot),
            };
            if (existingFlow.slots && flow.slots && existingFlow.mode === flow.mode) {
              flowWithPreservedContent = {
                ...flowWithPreservedContent,
                slots: flow.slots.map((newSlot) => {
                  const existingSlot = existingFlow.slots?.find((s) => s.id === newSlot.id);
                  // If existing slot has content (object) and new slot only has ID (string), preserve existing content
                  if (
                    existingSlot &&
                    existingSlot.content &&
                    typeof existingSlot.content === 'object' &&
                    (typeof newSlot.content === 'string' || !newSlot.content)
                  ) {
                    return { ...newSlot, content: existingSlot.content };
                  }
                  return newSlot;
                }),
              };
            }
            updatedFlows[flowIndex] = flowWithPreservedContent;
            setEventFlows(updatedFlows);
          }
        } else if (eventFlows.length > 0) {
          // Flow not in list but we have flows - add it (shouldn't happen normally, but handle it)
          // If eventSlotId is provided, use that specific eventSlot by ID only, otherwise find the first matching one
          const flowSlot =
            eventSlotIdParam && eventToUse
              ? eventToUse.slots?.find(
                  (slot: EventSlotDto) => slot.id === eventSlotIdParam && slot.contentType === EVENT_SLOT_TYPE.FLOW,
                )
              : eventToUse?.slots?.find(
                  (slot: EventSlotDto) =>
                    (slot.content === flow.id ||
                      slot.content === flow.origin ||
                      (typeof flow.origin === 'object' && flow.origin?.id && slot.content === flow.origin.id)) &&
                    slot.contentType === EVENT_SLOT_TYPE.FLOW,
                );
          setEventFlows([...eventFlows, { ...flow, slot: flowSlot }]);
        }
      }
    }
  }, [flow?.id, flow?.version, flow?.slots, event, setEventFlows, fetchedEvent, eventSlotIdParam]);

  useEffect(() => {
    if (contributors) setEventContributors(contributors);
  }, [contributors, setEventContributors]);

  // Handle flow load error - navigate to event flows list if event loads successfully
  useEffect(() => {
    // Only handle errors when flow has definitely failed (not loading, has error, and no flow data)
    if (isFlowError && !isFlowLoading && flowError && !flow && !hasNavigatedOnErrorRef.current) {
      // If we don't have an event ID to query (e.g., on page reload), fall back immediately
      if (!eventIdForQuery) {
        if (activeOrganization?.id) {
          hasNavigatedOnErrorRef.current = true;
          navigate(`/organization/${activeOrganization.id}/events`, { replace: true });
        }
        return;
      }

      // Wait for event query to complete
      if (isEventLoading) {
        return;
      }

      // If event loaded successfully, navigate to event flows list
      if (fetchedEvent && typeof fetchedEvent === 'object' && 'id' in fetchedEvent) {
        hasNavigatedOnErrorRef.current = true;
        navigate(`/event/${fetchedEvent.id}?tab=flows`, { replace: true });
        return;
      }

      // If event query also failed or returned no data, navigate to organization events list
      if (isEventError || !fetchedEvent) {
        if (activeOrganization?.id) {
          hasNavigatedOnErrorRef.current = true;
          navigate(`/organization/${activeOrganization.id}/events`, { replace: true });
        }
      }
    }
  }, [
    isFlowError,
    isFlowLoading,
    flowError,
    flow,
    fetchedEvent,
    navigate,
    isEventLoading,
    isEventError,
    eventIdForQuery,
    activeOrganization?.id,
  ]);

  // Reset navigation flag when flowId changes
  useEffect(() => {
    hasNavigatedOnErrorRef.current = false;
  }, [flowId]);

  useEffect(() => {
    if (!flowId) return;
    if (!flowMatchesRoute || !hasFreshFlowData) return;
    const currentFlow = useFlowStore.getState().flow;
    const currentFlowId =
      currentFlow?.origin && typeof currentFlow.origin !== 'string' ? currentFlow.origin.id : currentFlow?.origin;
    const resolvedCurrentFlowId = currentFlowId ?? currentFlow?.id;
    const planFlow = useFlowStore.getState().flowPlan;
    const organizeFlow = useFlowStore.getState().flowOrganize;
    const planFlowId = planFlow?.origin ? getObjectId(planFlow.origin) : planFlow?.id;
    const organizeFlowId = organizeFlow?.origin ? getObjectId(organizeFlow.origin) : organizeFlow?.id;
    const hasMismatchedStoredFlow =
      (resolvedCurrentFlowId && resolvedCurrentFlowId !== flowId) ||
      (planFlowId && planFlowId !== flowId) ||
      (organizeFlowId && organizeFlowId !== flowId);
    if (hasMismatchedStoredFlow) {
      setFlow(undefined);
      setSelectedSlot(undefined);
      useFlowStore.getState().setFlowPlan(undefined);
      useFlowStore.getState().setFlowOrganize(undefined);
    }
  }, [flowId, flowMatchesRoute, hasFreshFlowData, setFlow, setSelectedSlot]);

  useEffect(() => {
    if (!flowId) return;
    if (!flowMatchesRoute || !hasFreshFlowData) return;
    const currentFlow = useFlowStore.getState().flow;
    const currentFlowOriginId = currentFlow?.origin
      ? typeof currentFlow.origin === 'string'
        ? currentFlow.origin
        : currentFlow.origin.id
      : currentFlow?.id;
    if (currentFlowOriginId === flowId) return;

    const baseFlow = selectFlowByMode({
      flowMode,
      flowId,
      flowPlan,
      flowOrganize,
      fallbackFlow: flow,
    });
    if (baseFlow) {
      setFlow(baseFlow);
      const slotFromUrl = slotIdParam ? baseFlow.slots?.find((s) => s.id === slotIdParam) : undefined;
      const currentSlot = useFlowStore.getState().selectedSlot;
      const slotStillInFlow =
        currentSlot && baseFlow.slots ? findUpdatedSlotForSelection(baseFlow.slots, currentSlot) : undefined;
      if (slotFromUrl) {
        setSelectedSlot(slotFromUrl);
      } else if (slotStillInFlow) {
        // URL can lag behind setSearchParams; keep selection when refetch replaces store flow (e.g. after publish).
        setSelectedSlot(slotStillInFlow);
      } else {
        setSelectedSlot(undefined);
      }
    }
  }, [
    flowId,
    flow?.id,
    flowPlan?.id,
    flowOrganize?.id,
    flowMode,
    setFlow,
    setSelectedSlot,
    flowMatchesRoute,
    hasFreshFlowData,
    slotIdParam,
  ]);

  /** When URL still says view=slot&slotId=… but selection was cleared during refetch/sync, restore from flow. */
  useEffect(() => {
    if (!flowMatchesRoute || !hasFreshFlowData) return;
    if (viewParam !== 'slot' || !slotIdParam) return;
    const slots = useFlowStore.getState().flow?.slots ?? flow?.slots;
    if (!slots?.length) return;
    const match = slots.find((s) => s.id === slotIdParam);
    if (!match) return;
    const current = useFlowStore.getState().selectedSlot;
    if (current?.id === slotIdParam) return;
    setSelectedSlot(match);
  }, [viewParam, slotIdParam, flow?.slots, flowMatchesRoute, hasFreshFlowData, setSelectedSlot, flow]);

  useEffect(() => {
    if (!flowId) return;
    return () => {
      const nextFlowMode = { ...useModeStore.getState().flowMode };
      delete nextFlowMode[flowId];
      setMode({ flowMode: nextFlowMode });
    };
  }, [flowId, setMode]);

  useEffect(() => {
    if (eventSlotIdParam) return;
    const storeOriginId =
      storeFlow?.origin && typeof storeFlow.origin !== 'string'
        ? storeFlow.origin.id
        : (storeFlow?.origin ?? storeFlow?.id);
    const planOriginId = flowPlan?.origin ? getObjectId(flowPlan.origin) : flowPlan?.id;
    const organizeOriginId = flowOrganize?.origin ? getObjectId(flowOrganize.origin) : flowOrganize?.id;
    if (storeFlow?.slot && storeOriginId === flowId) {
      setFlow({ ...storeFlow, slot: undefined });
    }
    if (flowPlan?.slot && planOriginId === flowId) {
      setFlowPlan({ ...flowPlan, slot: undefined });
    }
    if (flowOrganize?.slot && organizeOriginId === flowId) {
      setFlowOrganize({ ...flowOrganize, slot: undefined });
    }
  }, [eventSlotIdParam, storeFlow, flowPlan, flowOrganize, setFlow, setFlowPlan, setFlowOrganize, flowId]);

  // Handle event load error - navigate to organization events list
  // Only if flow also failed (handled in flow error handler above) or if we're in a state where flow succeeded but event failed
  useEffect(() => {
    // Only navigate if flow has failed and we've already tried to get the event
    if (
      isEventError &&
      !isFlowLoading &&
      eventError &&
      isFlowError &&
      !hasNavigatedOnErrorRef.current &&
      activeOrganization?.id
    ) {
      hasNavigatedOnErrorRef.current = true;
      navigate(`/organization/${activeOrganization.id}/events`, { replace: true });
    }
  }, [isEventError, isFlowLoading, eventError, isFlowError, activeOrganization?.id, navigate]);

  const checkoutOrdersEventId = getOriginEventId(event as EventDto | undefined);

  // Show loading until flow data and mode are ready, or while waiting for error navigation
  if ((!isReady || !flowMatchesRoute || !hasFreshFlowData) && !isWaitingForErrorNavigation) {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
          }}
        >
          <CircularProgress />
        </Box>
        <PaymentProcessingModal eventId={checkoutOrdersEventId} />
      </>
    );
  }

  // If flow failed and we're waiting for navigation, show loading
  if (isWaitingForErrorNavigation) {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
          }}
        >
          <CircularProgress />
        </Box>
        <PaymentProcessingModal eventId={checkoutOrdersEventId} />
      </>
    );
  }

  return (
    <>
      <FixedNavigationBar context="flow" />
      <FlowBar
        setShowFlowDetails={handleFlowDetailsSelection}
        selectedSlot={selectedSlot}
        showFlowDetails={showFlowDetails}
      />
      <Box
        sx={{
          mt: `${flowBarHeight + 0}px`,
          pt: 0,
          height: {
            xs: `calc(100vh - 50px - 48px - ${flowBarHeight}px - 8px)`,
            md: `calc(100vh - 64px - 48px - ${flowBarHeight}px - 8px)`,
          },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Container
          sx={{ pt: 0, px: { xs: 1, sm: 2 }, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
          <Box sx={{ pt: 3, flexShrink: 0 }}>
            {(() => {
              const headerSx = { mb: showFlowDetails === 'flowControl' ? 1 : 2 };

              if (showFlowDetails === 'flowControl' && performTabsAllowed && flow) {
                return (
                  <FlowControlSectionHeader
                    flow={flow}
                    eventSlotId={eventSlotIdParam}
                    flowQueryKey={[GET_FLOW_QUERY_KEY, flowId, eventSlotIdParam ?? undefined]}
                    sx={headerSx}
                  />
                );
              }

              const sectionTitle =
                showFlowDetails === 'flowSettings'
                  ? t('flows.detail.editDetails.settingsTitle')
                  : showFlowDetails === 'flowSlotAssets'
                    ? t('flows.detail.editDetails.assetsTitle')
                    : showFlowDetails === 'flowContributors'
                      ? t('flows.detail.editDetails.contributorsTitle')
                      : showFlowDetails === 'flowConfiguration'
                        ? t('flows.detail.editDetails.configurationTitle')
                        : t('flows.detail.editDetails.scheduleTitle');

              if (showScheduleMainLayout) {
                return (
                  <FlowDetailSectionHeader
                    title={schedulePlanHeaderLabel}
                    accent={resolveFlowDetailHeaderAccent(schedulePlanHeaderColor)}
                    sx={headerSx}
                  />
                );
              }

              return (
                <FlowDetailSectionHeader
                  title={sectionTitle}
                  accent={resolveFlowDetailHeaderAccent('info')}
                  sx={headerSx}
                />
              );
            })()}
          </Box>
          {recoveryEntityRef ? (
            <FlowControlMediaAgentRecoveryAlert
              entityRef={recoveryEntityRef}
              flowId={flow ? resolveFlowEntityIdForPlayers(flow) : undefined}
              recoveryFromGet={liveContextStateForRecovery?.mediaAgentRecovery}
              liveContextState={liveContextStateForRecovery}
            />
          ) : null}
          <Box sx={{ display: 'flex', gap: 1, flex: 1, minHeight: 0 }}>
            {(() => {
              const scrollableBoxSx = {
                'flex': 1,
                'overflowY': 'auto' as const,
                'height': '100%',
                'px': 1,
                'pt': 0,
                '&::-webkit-scrollbar': { width: '8px' },
                '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '4px' },
              };

              if (showScheduleMainLayout) {
                return isWideScreen ? (
                  <FlowScheduleTab
                    setShowFlowDetails={handleFlowDetailsSelection}
                    setFlowDetailsDirection={setFlowDetailsDirection}
                    onSlotSelect={handleSlotSelection}
                    onShowTimingAlert={handleShowTimingAlert}
                    onShowPublishAlert={handleShowPublishAlert}
                    selectedSlot={selectedSlot}
                    slotTitle={slotTitle}
                    onSlotDeleted={handleSlotDeletedFromSchedule}
                    onSessionPublishSuccess={handleSessionPublishSuccessOnSchedule}
                    eventSlotIdParam={eventSlotIdParam}
                  />
                ) : (
                  <Box sx={{ ...slotListColumnOuterSx, width: '100%', flexShrink: 0, overflow: 'hidden' }}>
                    {(flowPlan ?? storeFlow) && eventSlotIdParam && eventPlan?.multiFlow !== false ? (
                      <Box sx={slotListColumnHeaderInsetSx}>
                        <FlowBasicsEventSlotTiming
                          flow={(flowPlan ?? storeFlow)!}
                          mode={flowModeValue}
                          disabled={isOrganizeMode(flowModeValue)}
                        />
                      </Box>
                    ) : null}
                    <Box sx={slotListScrollAreaSx}>
                      <SlotList
                        setShowFlowDetails={handleFlowDetailsSelection}
                        setFlowDetailsDirection={setFlowDetailsDirection}
                        onSlotSelect={handleSlotSelection}
                        onShowTimingAlert={handleShowTimingAlert}
                        onShowPublishAlert={handleShowPublishAlert}
                        middleSectionOpensOverlayOnSmallScreen
                        smallScreenInlineDetailsVariant="schedule"
                        onWideScreenSlotDeletedFromList={handleSlotDeletedFromSchedule}
                        onSessionPublishSuccess={handleSessionPublishSuccessOnSchedule}
                        hideFlowStartSlotRowWhenSmallScreen={SCHEDULE_SLOT_LIST_HIDE_FLOW_START_ON_SMALL_SCREEN}
                        hideEndItemMediaIndicator={SCHEDULE_SLOT_LIST_HIDE_END_ITEM_MEDIA_INDICATOR}
                        showSlotDragHandle={SCHEDULE_SLOT_LIST_SHOW_DRAG_HANDLE}
                        compactInfoAlert={SCHEDULE_SLOT_LIST_COMPACT_INFO_ALERT}
                      />
                    </Box>
                  </Box>
                );
              }

              if (showFlowDetails === 'flowSlotAssets') {
                if (isWideScreen) {
                  return (
                    <FlowSlotAssetsTab
                      setShowFlowDetails={handleFlowDetailsSelection}
                      setFlowDetailsDirection={setFlowDetailsDirection}
                      onSlotSelect={handleSlotSelection}
                      onShowTimingAlert={handleShowTimingAlert}
                      onShowPublishAlert={handleShowPublishAlert}
                      selectedSlot={selectedSlot}
                      slotTitle={slotTitle}
                      onSlotDeleted={() => handleFlowDetailsSelection('flowSettings')}
                      eventSlotIdParam={eventSlotIdParam}
                    />
                  );
                }
                return (
                  <Box
                    sx={{
                      width: '100%',
                      flexShrink: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      minHeight: 0,
                      height: '100%',
                    }}
                  >
                    <FlowSlotAssetsSlotListColumn
                      setShowFlowDetails={handleFlowDetailsSelection}
                      setFlowDetailsDirection={setFlowDetailsDirection}
                      onSlotSelect={handleSlotSelection}
                      onShowTimingAlert={handleShowTimingAlert}
                      onShowPublishAlert={handleShowPublishAlert}
                      middleSectionOpensOverlayOnSmallScreen
                    />
                  </Box>
                );
              }

              if (showFlowDetails === 'flowControl' && performTabsAllowed) {
                return (
                  <FlowControlTabErrorBoundary>
                    <FlowControlTab
                      setShowFlowDetails={handleFlowDetailsSelection}
                      setFlowDetailsDirection={setFlowDetailsDirection}
                      onSlotSelect={handleSlotSelection}
                      onShowTimingAlert={handleShowTimingAlert}
                      onShowPublishAlert={handleShowPublishAlert}
                      selectedSlot={selectedSlot}
                      onSlotDeleted={() => handleFlowDetailsSelection('flowSettings')}
                      eventSlotIdParam={eventSlotIdParam}
                      onRequestPerformOverlay={(mode) => {
                        if (mode) {
                          setNarrowViewMode(mode);
                        }
                        setOverlayExplicitlyOpened(true);
                      }}
                    />
                  </FlowControlTabErrorBoundary>
                );
              }

              if (
                showFlowDetails &&
                (showFlowDetails === 'flowSettings' ||
                  showFlowDetails === 'flowContributors' ||
                  (showFlowDetails === 'flowConfiguration' && performTabsAllowed))
              ) {
                return (
                  <Box sx={scrollableBoxSx}>
                    {showFlowDetails === 'flowSettings' && <FlowSettingsTab />}
                    {showFlowDetails === 'flowContributors' && <FlowContributorsTab />}
                    {showFlowDetails === 'flowConfiguration' && performTabsAllowed && <FlowConfigurationTab />}
                  </Box>
                );
              }

              // Fallback: small screen, no matching tab – show slot list so main area is never empty
              return (
                <Box sx={{ ...slotListColumnOuterSx, width: '100%', flexShrink: 0, overflow: 'hidden' }}>
                  <Box sx={slotListScrollAreaSx}>
                    <SlotList
                      setShowFlowDetails={handleFlowDetailsSelection}
                      setFlowDetailsDirection={setFlowDetailsDirection}
                      onSlotSelect={handleSlotSelection}
                      onShowTimingAlert={handleShowTimingAlert}
                      onShowPublishAlert={handleShowPublishAlert}
                    />
                  </Box>
                </Box>
              );
            })()}
          </Box>
          {!isWideScreen &&
            (overlayExplicitlyOpened ||
              (showFlowDetails !== undefined &&
                showFlowDetails !== 'flowControl' &&
                showFlowDetails !== 'flowSlotAssets' &&
                showFlowDetails !== 'flowSchedule' &&
                showFlowDetails !== 'flowSettings' &&
                showFlowDetails !== 'flowContributors' &&
                showFlowDetails !== 'flowConfiguration')) && (
              <EditDetailsDialog
                open={true}
                onClose={() => {
                  handleCloseDetails();
                  setFlowDetailsDirection(undefined);
                }}
                title={selectedSlot ? slotMobileTitle || slotTitle : (flow?.title ?? '')}
                subtitle={
                  selectedSlot
                    ? slotMobileSubtitle
                    : showFlowDetails === 'flowContributors'
                      ? t('flows.detail.editDetails.contributorsTitle')
                      : showFlowDetails === 'flowSettings'
                        ? t('flows.detail.editDetails.settingsTitle')
                        : showFlowDetails === 'flowSchedule'
                          ? t('flows.detail.editDetails.scheduleTitle')
                          : showFlowDetails === 'flowSlotAssets'
                            ? t('flows.detail.editDetails.assetsTitle')
                            : showFlowDetails === 'flowConfiguration'
                              ? t('flows.detail.editDetails.configurationTitle')
                              : showFlowDetails === 'flowControl'
                                ? t('flows.detail.editDetails.controlTitle')
                                : undefined
                }
                slot={selectedSlot}
                mode={showFlowDetails}
                onNavigateToFlowDetails={(direction) => {
                  setFlowDetailsDirection(direction);
                  handleFlowDetailsSelection('flowSlotAssets');
                }}
                onSlotChange={(slot) => {
                  handleSlotSelection(slot, true);
                }}
                cameFromDirectionProp={flowDetailsDirection}
                onSlotDeleted={handleSlotDeletedOnSmallScreen}
                eventSlotIdParam={eventSlotIdParam}
                setShowFlowDetails={handleFlowDetailsSelection}
                setFlowDetailsDirection={setFlowDetailsDirection}
                onShowTimingAlert={handleShowTimingAlert}
                onShowPublishAlert={handleShowPublishAlert}
                onSlotDeletedForControl={() => handleFlowDetailsSelection('flowSettings')}
              />
            )}
        </Container>
      </Box>
      <Snackbar
        open={showTimingAlert}
        autoHideDuration={1000}
        onClose={() => setShowTimingAlert(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowTimingAlert(false)} severity="info" variant="filled" sx={{ width: '100%' }}>
          {t('flows.detail.timingAlert.changeInPlanMode')}
        </Alert>
      </Snackbar>
      <Snackbar
        open={showPublishAlert}
        autoHideDuration={1000}
        onClose={() => setShowPublishAlert(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowPublishAlert(false)} severity="info" variant="filled" sx={{ width: '100%' }}>
          {t('flows.detail.publishAlert.editMode')}
        </Alert>
      </Snackbar>
      <PaymentProcessingModal eventId={checkoutOrdersEventId} />
    </>
  );
}
